import { cn } from '@/lib/utils';
import type { ChatCitation } from '../types/chatStream';
import { mcgrawHillReferenceById } from './mcgrawHillReferences';
import { splitSourceTitle, type McGrawHillSource } from './mcgrawHillCitations';

interface McGrawHillSourcesProps {
  sources: readonly McGrawHillSource[];
  /** Opens a source's page (the same path a source chip takes). */
  onOpen?: (citation: ChatCitation) => void;
}

/**
 * A heading label, or none when the source only knows its page. A label that
 * repeats the document's title (the default "<title> · p. N") is no heading.
 */
const sectionOf = (citation: ChatCitation): string | null => {
  let label = citation.label?.trim() ?? '';
  if (citation.title && label.startsWith(citation.title)) {
    label = label.slice(citation.title.length).replace(/^\s*·\s*/, '');
  }
  if (!label || /^p\.\s*\d+$/i.test(label)) return null;
  return label;
};

/**
 * mcgraw-hill: an AI desktop answer's numbered sources, in place of the
 * source chips: the number its badges carry, the book, the chapter, the
 * section it drew on, and a link that opens the page.
 */
const McGrawHillSources = ({ sources, onOpen }: McGrawHillSourcesProps) => {
  if (sources.length === 0) return null;

  return (
    <section aria-label="Sources" className="mcgraw-hill-sources">
      <h3 className="mcgraw-hill-sources__heading">Sources</h3>
      <ol className="mcgraw-hill-sources__list">
        {sources.map(({ number, citation }) => {
          const { book, rest } = splitSourceTitle(citation.title);
          const section = sectionOf(citation);
          const page = citation.page ? `p. ${citation.page}` : null;

          const isArticle =
            mcgrawHillReferenceById(citation.documentId)?.role === 'article';

          return (
            <li key={citation.id} className="mcgraw-hill-sources__item">
              <span
                className={cn(
                  'mcgraw-hill-cite mcgraw-hill-cite--list',
                  isArticle && 'mcgraw-hill-cite--article'
                )}
              >
                {number}
              </span>
              <span
                className={cn(
                  'mcgraw-hill-sources__mark',
                  isArticle && 'mcgraw-hill-sources__mark--article'
                )}
                aria-hidden="true"
              />
              <span className="mcgraw-hill-sources__text">
                <strong>{book}</strong>
                {rest}
                {section || page ? (
                  <span className="mcgraw-hill-sources__section">
                    {' · '}
                    {section ?? page}
                  </span>
                ) : null}
              </span>
              {onOpen ? (
                <button
                  type="button"
                  className="mcgraw-hill-sources__open"
                  onClick={() =>
                    onOpen({ ...citation, highlightExcerpt: true })
                  }
                  aria-label={`Open source ${number}`}
                >
                  open <span aria-hidden="true">↗</span>
                </button>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default McGrawHillSources;
